import { useEffect, useRef } from "react";

/**
 * Fixed-position full-viewport canvas that draws ~120 slow-drift particles.
 * Cheap (canvas 2D, no dependencies), dpr-aware, respects prefers-reduced-motion,
 * and pauses when the tab is hidden.
 */
export function BackgroundParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const COUNT = reduced ? 40 : 120;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = window.innerWidth;
    let height = window.innerHeight;

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas!.width = width * dpr;
      canvas!.height = height * dpr;
      canvas!.style.width = width + "px";
      canvas!.style.height = height + "px";
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    // Three "layers" — closer particles are bigger + brighter + move faster
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      hue: "cyan" | "magenta" | "violet";
      alpha: number;
      layer: number;
    }

    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const particles: Particle[] = Array.from({ length: COUNT }, () => {
      const layer = Math.floor(rnd(0, 3));
      return {
        x: rnd(0, width),
        y: rnd(0, height),
        vx: rnd(-0.06, 0.06) * (layer + 1),
        vy: rnd(-0.04, 0.04) * (layer + 1),
        r: rnd(0.4, 1.2) + layer * 0.3,
        hue: (["cyan", "magenta", "violet"] as const)[Math.floor(rnd(0, 3))],
        alpha: rnd(0.15, 0.55),
        layer,
      };
    });

    const colors: Record<Particle["hue"], string> = {
      cyan:    "0, 212, 255",
      magenta: "255, 46, 136",
      violet:  "168, 85, 247",
    };

    let raf = 0;
    let running = true;

    function tick() {
      if (!running) return;
      ctx!.clearRect(0, 0, width, height);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        // Wrap
        if (p.x < -5) p.x = width + 5;
        if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5;
        if (p.y > height + 5) p.y = -5;

        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${colors[p.hue]}, ${p.alpha})`;
        ctx!.fill();

        // Halo for larger particles
        if (p.layer >= 1) {
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
          const grad = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
          grad.addColorStop(0, `rgba(${colors[p.hue]}, ${p.alpha * 0.25})`);
          grad.addColorStop(1, `rgba(${colors[p.hue]}, 0)`);
          ctx!.fillStyle = grad;
          ctx!.fill();
        }
      }
      raf = requestAnimationFrame(tick);
    }
    tick();

    function onVis() {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        tick();
      }
    }

    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="fixed inset-0 pointer-events-none z-0"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
